import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, FileText, AlertTriangle, FolderOpen, User } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { FileAzioni } from "./FileViewer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const CATS = [
  "Documento di identità",
  "Tessera sanitaria",
  "Cartella clinica",
  "Certificato di autosufficienza",
  "Piano terapeutico",
  "Prescrizioni",
  "Presidi",
  "Allergie",
  "Esenzione",
  "Contratto con la struttura",
  "Documentazione ADI",
  "Consenso privacy",
  "Altri documenti personalizzati",
];
const CUSTOM = "Altri documenti personalizzati";
const CONV_CAT = "Convenzione di ospitalità firmata";
const OBBL = [["Certificato di autosufficienza", ""], ["Contratto con la struttura", "La convenzione firmata dalla scheda Convenzione arriva qui da sola."]];
const catOf = (d) => ({ "Certificato di autonomia": "Certificato di autosufficienza", [CONV_CAT]: "Contratto con la struttura" }[d.categoria] || d.categoria || CUSTOM);
const slug = (c) => c.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const isExpiring = (sc) => sc && sc <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

export default function DocumentiOspite({ ospite, smart }) {
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("tutti");
  const [docs, setDocs] = useState(null);
  const [operatori, setOperatori] = useState([]);
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState(null);
  const [meta, setMeta] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const qc = useQueryClient();
  const load = async () => { const { data } = await api.get(`/ospiti/${ospite.id}/documenti`); setDocs(data); qc.invalidateQueries({ queryKey: ["osp-doc-obbl"] }); };
  useEffect(() => { load(); }, [ospite.id]);
  useEffect(() => { if (ospite.structure_id) api.get("/firma/operatori", { params: { structure_id: ospite.structure_id } }).then((r) => setOperatori(r.data)); }, [ospite.structure_id]);

  const loaded = docs !== null;
  const allDocs = docs || [];
  const byCat = (c) => allDocs.filter((d) => catOf(d) === c);
  const altri = allDocs.filter((d) => !OBBL.some(([c]) => c === catOf(d)));
  const isCustom = cat === CUSTOM;

  const openUpload = (c) => { setCat(c); setMeta({}); if (fileRef.current) fileRef.current.value = ""; setOpen(true); };
  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona un file"); return; }
    if (!cat) { toast.error("Scegli il tipo di documento"); return; }
    if (!meta.operatore_nome) { toast.error("Seleziona l'operatore che carica il documento"); return; }
    if (isCustom && !meta.titolo?.trim()) { toast.error("Inserisci un titolo per il documento personalizzato"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", "ospite");
      fd.append("owner_id", ospite.id);
      fd.append("owner_nome", `${ospite.nome} ${ospite.cognome}`);
      if (ospite.structure_id) fd.append("structure_id", ospite.structure_id);
      fd.append("categoria", cat);
      fd.append("operatore_nome", meta.operatore_nome);
      if (meta.titolo) fd.append("titolo", meta.titolo);
      if (meta.data_documento) fd.append("data_documento", meta.data_documento);
      if (meta.data_scadenza) fd.append("data_scadenza", meta.data_scadenza);
      if (meta.note) fd.append("note", meta.note);
      await api.post("/documenti/upload", fd);
      toast.success("Documento caricato");
      setOpen(false); setMeta({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => { try { await api.delete(`/documenti/${id}`); toast.success("Documento eliminato"); load(); } catch { toast.error("Errore"); } };

  const docRow = (d) => (
    <div key={d.id} className="rounded-lg border bg-white p-2" data-testid={`doc-${d.id}`}>
      <div className="flex items-start gap-2">
        <FileText size={15} className="text-primary shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-xs truncate">{d.titolo || d.original_filename}</div>
          <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
            <User size={10} /> {smart ? `${catOf(d)} · ` : ""}{d.operatore_nome || d.created_by || "—"} · {fmtDate(d.created_at)}
          </div>
          {d.data_scadenza && <div className={`text-[11px] ${isExpiring(d.data_scadenza) ? "text-red-600 font-bold" : "text-muted-foreground"}`}>{isExpiring(d.data_scadenza) && <AlertTriangle size={10} className="inline mr-0.5" />}Scade {fmtDate(d.data_scadenza)}</div>}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <FileAzioni url={`/documenti/${d.id}/download`} titolo={d.original_filename || d.titolo || "documento"} testid={d.id} size={14} />
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(d.id)} data-testid={`deldoc-${d.id}`}><Trash2 size={14} className="text-destructive" /></Button>
        </div>
      </div>
    </div>
  );
  const inScad = allDocs.filter((d) => isExpiring(d.data_scadenza));
  const filtrati = altri.filter((d) => (fCat === "tutti" || catOf(d) === fCat) && (!q.trim() || `${d.titolo || ""} ${d.original_filename || ""} ${d.categoria || ""}`.toLowerCase().includes(q.trim().toLowerCase())));
  const obbligatori = (
    <div className="mb-4" data-testid="doc-obbligatori">
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-800 mb-2">Documenti obbligatori</div>
      <div className="grid md:grid-cols-2 gap-3">
        {OBBL.map(([c, hint]) => {
          const items = byCat(c);
          const ok = items.length > 0;
          if (!loaded) return <div key={c} className="rounded-xl border-2 bg-white p-3 text-sm text-muted-foreground">Caricamento…</div>;
          return (
            <div key={c} className={`rounded-xl border-2 bg-white p-3 ${ok ? "border-emerald-300" : "border-red-300"}`} data-testid={`doc-obbl-${slug(c)}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <FolderOpen size={15} className="text-primary shrink-0" /><b className="text-sm">{c}</b>
                <span className={`ml-auto text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`doc-obbl-stato-${slug(c)}`}>{ok ? "Caricato" : "Mancante"}</span>
              </div>
              {hint && <p className="text-[11px] text-muted-foreground mb-2">{hint}</p>}
              <div className="space-y-1.5">{items.map(docRow)}</div>
              <Button size="sm" variant={ok ? "outline" : "default"} className="w-full h-8 text-xs font-semibold mt-2" onClick={() => openUpload(c)} data-testid={`upload-${slug(c)}`}><Upload size={13} className="mr-1" /> Carica</Button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      {smart ? (
        <div className="space-y-4" data-testid="documenti-smart">
          {obbligatori}
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-800">Altri documenti</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un documento…" className="flex-1 min-w-[200px] h-10 bg-white" data-testid="doc-cerca" />
            <Button onClick={() => openUpload(null)} className="h-10 font-semibold" data-testid="doc-carica"><Upload size={15} className="mr-1" /> Carica documento</Button>
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="doc-filtri">
            {["tutti", ...CATS.filter((c) => byCat(c).length && !OBBL.some(([o]) => o === c))].map((c) => (
              <button key={c} type="button" onClick={() => setFCat(c)} data-testid={`doc-filtro-${slug(c)}`} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${fCat === c ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-slate-600 hover:bg-emerald-50"}`}>{c === "tutti" ? "Tutti" : c} {c === "tutti" ? altri.length : byCat(c).length}</button>
            ))}
          </div>
          {inScad.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="doc-scadenza-avviso"><b>Da rinnovare:</b> {inScad.map((d) => `${d.titolo || d.original_filename} (scade il ${fmtDate(d.data_scadenza)})`).join(" · ")}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtrati.length === 0 && <p className="text-sm text-muted-foreground italic">Nessun documento.</p>}
            {filtrati.map(docRow)}
          </div>
        </div>
      ) : (<>
      {obbligatori}
      <div className="mb-4 text-sm text-muted-foreground">Documenti di <span className="font-semibold text-foreground">{ospite.nome} {ospite.cognome}</span> · organizzati per tipologia. Ogni caricamento richiede l'operatore.</div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {CATS.filter((c) => !OBBL.some(([o]) => o === c)).map((c) => {
          const items = byCat(c);
          return (
            <div key={c} className="rounded-xl border overflow-hidden flex flex-col" data-testid={`doccat-${slug(c)}`}>
              <div className="px-3 py-2 bg-secondary/40 border-b flex items-center gap-2">
                <FolderOpen size={15} className="text-primary shrink-0" />
                <span className="font-semibold text-sm leading-tight">{c}</span>
                <span className="ml-auto text-xs font-bold text-muted-foreground tabnum" data-testid={`doccat-count-${slug(c)}`}>{items.length}</span>
              </div>
              <div className="p-2 space-y-1.5 flex-1">
                {items.length === 0 && <p className="text-xs text-muted-foreground py-2 px-1">Nessun documento</p>}
                {items.map(docRow)}
                <Button size="sm" variant="outline" className="w-full h-8 text-xs font-semibold mt-1" onClick={() => openUpload(c)} data-testid={`upload-${slug(c)}`}><Upload size={13} className="mr-1" /> Carica</Button>
              </div>
            </div>
          );
        })}
      </div>
      </>)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica documento</DialogTitle>
            <DialogDescription>{cat || "Scegli il tipo di documento"} — l'operatore che carica è obbligatorio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {smart && <div><Label className="text-xs font-semibold mb-1 block">Tipo di documento *</Label>
              <Select value={cat || ""} onValueChange={setCat}>
                <SelectTrigger data-testid="doc-categoria"><SelectValue placeholder="Seleziona il tipo" /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>}
            <div><Label className="text-xs font-semibold mb-1 block">File *</Label><Input type="file" ref={fileRef} data-testid="doc-file" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Operatore che carica *</Label>
              <Select value={meta.operatore_nome || ""} onValueChange={(v) => setMeta((p) => ({ ...p, operatore_nome: v }))}>
                <SelectTrigger data-testid="doc-operatore"><SelectValue placeholder={operatori.length ? "Seleziona operatore" : "Nessun operatore per la struttura"} /></SelectTrigger>
                <SelectContent>{operatori.map((op) => <SelectItem key={op.id} value={`${op.nome} ${op.cognome}`}>{op.nome} {op.cognome} ({op.qualifica})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Titolo {isCustom ? "*" : "(opzionale)"}</Label><Input value={meta.titolo || ""} onChange={(e) => setMeta((p) => ({ ...p, titolo: e.target.value }))} placeholder={isCustom ? "Es. Verbale familiari" : "Lascia vuoto per usare il nome file"} data-testid="doc-titolo" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">Data documento</Label><Input type="date" value={meta.data_documento || ""} onChange={(e) => setMeta((p) => ({ ...p, data_documento: e.target.value }))} /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Data scadenza</Label><Input type="date" value={meta.data_scadenza || ""} onChange={(e) => setMeta((p) => ({ ...p, data_scadenza: e.target.value }))} data-testid="doc-scadenza" /></div>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Note</Label><Input value={meta.note || ""} onChange={(e) => setMeta((p) => ({ ...p, note: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={upload} disabled={uploading} data-testid="doc-save" className="font-semibold">{uploading ? "Caricamento..." : "Carica"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
